import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

interface HasQueryKeydownHandler {
  queryKeydownHandler: (e: ElementKeyboardEvent) => boolean;
}

interface ControlledSearchInputProps<T extends HasQueryKeydownHandler> {
  shouldForwardKeydown: boolean;
  canSelectAdditional: boolean;
  hasTypedValue: boolean;
  onRemoveLastValue: () => void;
  autoFocus: boolean;
  inputValue: string;
  onBlur?: () => void;
  onUpdateInputValue: (inputValue: string) => void;
  onKeyDown?: ((e: ElementKeyboardEvent) => void) | null;
  renderSelectedValues: () => JSX.Element | null;
  // This dropdown should either use AttachDropdown or be wrapped in AttachDropdownOuterWrapper so that
  // its appearance and z-index positioning are appropriately styled.
  renderSearchDropdown?: (ref: React.RefObject<T>) => JSX.Element | null;
  shouldCloseOnInsideClick: boolean;
  placeholder: string;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;
  // Whether to enable an improved UX and show a caret to the side of the dropdown input
  showCaret?: boolean;
}

interface State {
  isInputFocused: boolean;
}

/**
 * Inline search input designed for table cells.
 */
class ControlledSearchInput<T extends HasQueryKeydownHandler> extends React.Component<
  ControlledSearchInputProps<T>,
  State
> {
  attachDropdownRef = React.createRef<T>();

  state: State = {
    isInputFocused: false,
  };

  private handleTextInputKeyDown = (e: ElementKeyboardEvent): void => {
    if (this.props.shouldForwardKeydown) {
      // TODO(T9848): Revisit this pattern of calling the keydown handler from the parent component.
      if (this.attachDropdownRef.current && this.attachDropdownRef.current.queryKeydownHandler(e)) {
        // queryKeydownHandler returns true if the attachDropdown has successfully handled the keydown event
        return;
      }
    }

    if (this.props.inputValue.length === 0 && this.props.hasTypedValue && keyboard.isADeleteKey(e)) {
      this.props.onRemoveLastValue();
    } else {
      // Regrab focus if user is typing and focus was lost.
      if (!this.state.isInputFocused) {
        this.handleTextInputFocus();
      }
      if (this.props.onKeyDown) {
        this.props.onKeyDown(e);
      }
    }
  };

  private handleUpdateText = (e: InputChangeEvent): void => {
    if (!this.props.canSelectAdditional) {
      // Block typing if can't select additional
      return;
    }
    this.props.onUpdateInputValue(e.target.value);
  };

  private handleTextInputFocus = (): void => {
    this.setState({isInputFocused: true});
  };

  private handleTextInputBlur = (): void => {
    this.setState({isInputFocused: false});
    this.props.onBlur?.();
  };

  render(): JSX.Element {
    const input = (
      <input
        autoFocus={this.props.autoFocus}
        className={classNames(
          'searchInput-input',
          !this.props.canSelectAdditional && this.props.isOutsideTable && 'searchInput-input--narrow'
        )}
        onKeyDown={this.handleTextInputKeyDown}
        onChange={this.handleUpdateText}
        onFocus={this.handleTextInputFocus}
        onBlur={this.handleTextInputBlur}
        value={this.props.inputValue}
        placeholder={this.props.placeholder}
      />
    );

    // We allow opening the dropdown when either it allows multiple selections, or if showCaret is true (improved UX)
    const isOpen = this.state.isInputFocused && (this.props.showCaret || this.props.canSelectAdditional);

    const child = (
      <span>
        {this.props.renderSelectedValues()}
        {this.props.isOutsideTable ? input : <div className="searchInput-row">{input}</div>}
        {this.props.showCaret && (
          <div className={css(styles.buttonCaretContainer)}>
            <BenchlingIcon
              className={css(styles.buttonCaret)}
              icon={isOpen ? 'chevron-up' : 'chevron-down'}
              onClick={this.handleTextInputFocus}
            />
          </div>
        )}
      </span>
    );

    if (this.props.renderSearchDropdown) {
      return (
        <AbsoluteDropdownContainer
          canDropUnder={true}
          menu={this.props.renderSearchDropdown(this.attachDropdownRef)}
          onRequestClose={this.handleTextInputBlur}
          open={isOpen}
          shouldCloseOnInsideClick={this.props.shouldCloseOnInsideClick}
        >
          {child}
        </AbsoluteDropdownContainer>
      );
    } else {
      return child;
    }
  }
}

const styles = StyleSheet.create({
  buttonCaretContainer: {
    position: 'absolute',
    right: 10,
    top: '50%',
    lineHeight: '1em',
    marginTop: -6,
    marginLeft: 5,
  },
  buttonCaret: {
    color: GRAY_DARKER,
    cursor: 'pointer',
  },
});

export default ControlledSearchInput;
